import { Box, Chip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useLayoutEffect, useRef, useState } from "react";
import { UserRow } from "components/users/userAdminFilters";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { AccountType } from "store/api/generatedApi";
import { CsvColumnDef } from "src/utils/csv";
import { countChipsThatFit } from "src/utils/chipFitting";
import { userDisplayName } from "src/utils/userDisplay";

const CHIP_GAP_PX = 4;
const OVERFLOW_CHIP_WIDTH_PX = 46;

const PermissionChips = ({
  permissions,
}: {
  permissions: UserRow["permissions"];
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(permissions.length);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const measurer = measureRef.current;
    if (!wrapper || !measurer) return;

    const recompute = () => {
      const chipWidths = Array.from(measurer.children).map(
        (child) => (child as HTMLElement).offsetWidth
      );
      setVisibleCount(
        countChipsThatFit({
          widths: chipWidths,
          availableWidth: wrapper.clientWidth,
          overflowWidth: OVERFLOW_CHIP_WIDTH_PX,
          gapWidth: CHIP_GAP_PX,
        })
      );
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [permissions]);

  const overflowCount = permissions.length - visibleCount;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          display: "flex",
          gap: 0.5,
          pointerEvents: "none",
        }}
      >
        {permissions.map((userPermission) => (
          <Chip
            key={userPermission.permission}
            size="small"
            variant="outlined"
            label={userPermission.permission}
          />
        ))}
      </Box>
      {permissions.slice(0, visibleCount).map((userPermission) => (
        <Chip
          key={userPermission.permission}
          size="small"
          variant="outlined"
          label={userPermission.permission}
        />
      ))}
      {overflowCount > 0 ? (
        <Chip size="small" label={`+${overflowCount}`} />
      ) : null}
    </Box>
  );
};

const statusSortKey = (row: UserRow): string =>
  row.is_admin ? "admin" : !row.is_enabled ? "disabled" : "active";

export const useUserGridColumns = ({
  accountType,
  onCreateInviteLink,
  onSendInviteEmail,
}: {
  accountType: AccountType;
  onCreateInviteLink: (email: string) => void;
  onSendInviteEmail: (email: string) => void;
}): GridColDef<UserRow>[] => {
  const { t } = useTenantTranslation();
  const formatDate = useFormatLocalDateString("date");

  return [
    {
      field: "name",
      headerName: t("name"),
      flex: 1.2,
      valueGetter: (_value, row) => userDisplayName(row),
    },
    {
      field: "email",
      headerName: t("email"),
      flex: 1.5,
    },
    {
      field: "status",
      headerName: t("status"),
      width: 130,
      valueGetter: (_value, row) => statusSortKey(row),
      renderCell: ({ row }) => {
        if (row.is_admin) {
          return <Chip size="small" color="primary" label={t("admin")} />;
        }
        if (!row.is_enabled) {
          return <Chip size="small" color="error" label={t("disabled")} />;
        }
        return <Chip size="small" color="success" label={t("active")} />;
      },
    },
    {
      field: "permissions",
      headerName: t("permissions"),
      flex: 2,
      sortable: true,
      valueGetter: (_value, row) => row.permissions.length,
      renderCell: ({ row }) =>
        row.permissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("noPermissions")}
          </Typography>
        ) : (
          <PermissionChips permissions={row.permissions} />
        ),
    },
    {
      field: "updated_at",
      headerName: t("updatedAt"),
      width: 150,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: "actions",
      type: "actions",
      width: 60,
      getActions: ({ row }) =>
        accountType === "user"
          ? [
              <GridActionsCellItem
                key="link"
                label={t("createInviteLink")}
                onClick={() => {
                  onCreateInviteLink(row.email);
                }}
                showInMenu
              />,
              <GridActionsCellItem
                key="email"
                label={t("sendInviteEmail")}
                onClick={() => {
                  onSendInviteEmail(row.email);
                }}
                showInMenu
              />,
            ]
          : [],
    },
  ];
};

export const useUserCsvColumns = (): CsvColumnDef<UserRow>[] => {
  const { t } = useTenantTranslation();
  return [
    {
      header: t("name"),
      value: (row) => userDisplayName(row),
    },
    { header: t("email"), value: (row) => row.email },
    { header: t("status"), value: (row) => statusSortKey(row) },
    {
      header: t("permissions"),
      value: (row) =>
        row.permissions
          .map((userPermission) => userPermission.permission)
          .join("; "),
    },
    { header: t("updatedAt"), value: (row) => row.updated_at },
  ];
};
